import { z } from 'zod'
import { uuidSchema } from './common.ts'
import { warehouseUnitCodeSchema } from './units.ts'
import {
  additionalCostUahSchema,
  manualExchangeRateInputSchema,
  positiveStockQuantitySchema,
  receiptUnitPriceSchema,
} from './warehouse.ts'

/**
 * ТТН (waybill) extraction contract.
 *
 * Lives in @veem/schema rather than @veem/ai because three places need the same shape:
 * the model's structured output, the storekeeper's confirmation screen, and the endpoint
 * that turns a confirmed draft into stock_moves rows.
 *
 * Every field is nullable. A phone photo of a carbon-copy form in a workshop will not yield
 * a complete record, and a model that invents a quantity to satisfy a required field is far
 * worse than one that returns null and asks a human.
 */
export const waybillLineSchema = z.object({
  /** Raw material name exactly as printed. Matched to a `materials` row by a human. */
  rawName: z.string().nullable(),
  quantity: z.string().nullable().describe('decimal string as printed, e.g. "12.400"'),
  unit: warehouseUnitCodeSchema.nullable(),
  pricePerUnit: z.string().nullable(),
  totalPrice: z.string().nullable(),
  /**
   * Per-line confidence. Printed table rows read reliably; handwritten quantities and
   * stamp-obscured text do not, and the review UI sorts by this so the storekeeper
   * checks the doubtful lines first.
   */
  confidence: z.enum(['high', 'medium', 'low']),
})

export type WaybillLine = z.infer<typeof waybillLineSchema>

export const waybillExtractionSchema = z.object({
  ttnNumber: z.string().nullable(),
  issuedAt: z.string().nullable().describe('date as printed, e.g. "12.06.2026"'),
  supplierName: z.string().nullable(),
  lines: z.array(waybillLineSchema),
  /** Anything the model could not read — surfaced verbatim to the storekeeper. */
  unreadableNotes: z.array(z.string()),
})

export type WaybillExtraction = z.infer<typeof waybillExtractionSchema>

/**
 * A draft is never posted to the stock ledger automatically. `confirmedBy` is set only after
 * a human reviews it — see docs/02-open-questions.md item 3, which we must settle with VEEM
 * in writing.
 */
export const waybillDraftStatusSchema = z.enum(['pending_review', 'confirmed', 'rejected'])
export type WaybillDraftStatus = z.infer<typeof waybillDraftStatusSchema>

export const waybillDocumentTypeSchema = z.enum(['inbound_supplier_waybill', 'other'])
export type WaybillDocumentType = z.infer<typeof waybillDocumentTypeSchema>

const acceptedWaybillLineSchema = z
  .object({
    lineIndex: z.int().nonnegative(),
    reviewState: z.literal('accepted'),
    materialId: uuidSchema,
    quantity: positiveStockQuantitySchema,
    unit: warehouseUnitCodeSchema,
    unitPrice: receiptUnitPriceSchema,
    currency: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{3}$/)
      .toUpperCase(),
    additionalCostUah: additionalCostUahSchema.optional(),
    manualRate: manualExchangeRateInputSchema.optional(),
  })
  .superRefine((line, context) => {
    if (line.currency === 'UAH' && line.manualRate)
      context.addIssue({
        code: 'custom',
        path: ['manualRate'],
        message: 'Для UAH курс завжди дорівнює 1 за офіційним правилом НБУ',
      })
  })

const excludedWaybillLineSchema = z.object({
  lineIndex: z.int().nonnegative(),
  reviewState: z.literal('excluded'),
})

export const reviewedWaybillLineSchema = z.discriminatedUnion('reviewState', [
  acceptedWaybillLineSchema,
  excludedWaybillLineSchema,
])
export type ReviewedWaybillLine = z.infer<typeof reviewedWaybillLineSchema>

export const confirmWaybillDraftInputSchema = z.object({
  documentType: z.literal('inbound_supplier_waybill'),
  ttnNumber: z.string().trim().min(1).max(240),
  supplierId: uuidSchema,
  buyingSellerEntityId: uuidSchema,
  locationId: uuidSchema,
  receiptDate: z.iso.date(),
  lines: z.array(reviewedWaybillLineSchema).min(1),
})

export const rejectWaybillDraftInputSchema = z.object({
  documentType: waybillDocumentTypeSchema,
  note: z.string().trim().min(1).max(1000),
})

const waybillSnapshotSourceLineSchema = waybillLineSchema.extend({
  lineIndex: z.int().nonnegative(),
})
const waybillSnapshotAcceptedLineSchema = z.object({
  lineIndex: z.int().nonnegative(),
  reviewState: z.literal('accepted'),
  source: waybillSnapshotSourceLineSchema,
  material: z.object({
    id: uuidSchema,
    name: z.string(),
    grade: z.string().nullable(),
    baseUnit: warehouseUnitCodeSchema,
  }),
  quantity: positiveStockQuantitySchema,
  unit: z.object({ code: warehouseUnitCodeSchema, name: z.string(), symbol: z.string() }),
  unitPrice: receiptUnitPriceSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  additionalCostUah: additionalCostUahSchema,
  rate: z.object({
    currency: z.string().regex(/^[A-Z]{3}$/),
    requestedRateDate: z.iso.date(),
    effectiveRateDate: z.iso.date(),
    rate: z.string(),
    units: z.string(),
    provider: z.enum(['NBU', 'manual']),
    sourceUrl: z.string(),
    fetchedAt: z.string(),
    manualReason: z.string().nullable(),
  }),
})
const waybillSnapshotExcludedLineSchema = z.object({
  lineIndex: z.int().nonnegative(),
  reviewState: z.literal('excluded'),
  source: waybillSnapshotSourceLineSchema,
})

export const waybillReviewSnapshotSchema = z.object({
  version: z.literal(1),
  documentType: waybillDocumentTypeSchema,
  ttnNumber: z.string().nullable(),
  receiptDate: z.iso.date().nullable(),
  rejectionReason: z.string().nullable(),
  supplier: z
    .object({
      id: uuidSchema,
      displayName: z.string(),
      legalName: z.string().nullable(),
      edrpou: z.string().nullable(),
      contactName: z.string().nullable(),
      phone: z.string().nullable(),
      email: z.string().nullable(),
      address: z.string().nullable(),
    })
    .nullable(),
  buyer: z.object({ id: uuidSchema, name: z.string(), edrpou: z.string().nullable() }).nullable(),
  location: z
    .object({
      id: uuidSchema,
      name: z.string(),
      warehouseId: uuidSchema,
      warehouseName: z.string(),
    })
    .nullable(),
  lines: z.array(
    z.discriminatedUnion('reviewState', [
      waybillSnapshotAcceptedLineSchema,
      waybillSnapshotExcludedLineSchema,
    ]),
  ),
})
export type WaybillReviewSnapshot = z.infer<typeof waybillReviewSnapshotSchema>
